import { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

const PDF_ENDPOINT = "/FillMyForm/BOM_AOF1/SinglePrint.php";
const STORAGE_KEY = "smart-solutions-bom-single";

const choices = {
  accountType: [["SELECT", "SELECT"], ["SAVING", "SAVING"], ["CURRENT", "CURRENT"]],
  gender: [["SELECT", "SELECT"], ["MALE", "MALE"], ["FEMALE", "FEMALE"]],
  marital: [["SELECT", "SELECT"], ["MARRIED", "MARRIED"], ["UNMARRIED", "UNMARRIED"]],
  guardianRelation: [["SELECT", "SELECT"], ["FATHER", "FATHER"], ["MOTHER", "MOTHER"], ["BROTHER", "BROTHER"], ["SISTER", "SISTER"], ["OTHER", "OTHER"]],
  religion: [["SELECT", "SELECT"], ["HINDU", "HINDU"], ["MUSLIM", "MUSLIM"], ["CHRISTIAN", "CHRISTIAN"], ["SIKH", "SIKH"]],
  category: [["SELECT", "SELECT"], ["GENERAL", "GENERAL"], ["OBC", "OBC"], ["SC", "SC"], ["ST", "ST"]],
  qualification: [["SELECT", "SELECT"], ["ILLITERATE", "Illiterate"], ["BSSC", "Below SSC"], ["SSC", "SSC"], ["HSC", "HSC"], ["GRADUATE", "Graduate"], ["PGRADUATE", "Post Graduate"], ["PROFFESIONAL", "Professional"], ["OTHERS", "Others"]],
  occupation: [["SELECT", "SELECT"], ["SERVICE", "Service"], ["PRIVATE_SECTOR", "Private Sector"], ["PUBLIC_SECTOR", "Public Sector"], ["GOVERNMENT_SECTOR", "Government Sector"], ["OTHERS", "Others"], ["PROFESSIONAL", "Professional"], ["SELF_EMPLOYED", "Self Employed"], ["RETIRED", "Retired"], ["HOUSEWIFE", "Housewife"], ["STUDENT", "Student"], ["BUSINESS", "Business"], ["NOT_CATEGORISED", "Not categorised"]],
  nomineeRelation: [["SELECT", "SELECT"], ["FATHER", "FATHER"], ["MOTHER", "MOTHER"], ["HUSBAND", "HUSBAND"], ["WIFE", "WIFE"], ["SON", "SON"], ["DAUGHTER", "DAUGHTER"], ["BROTHER", "BROTHER"], ["SISTER", "SISTER"], ["OTHER", "OTHER"]],
};

const personalFields = [
  { name: "branch", label: "Branch Name", maxLength: 18 },
  { name: "code", label: "Branch Code", maxLength: 5 },
  { name: "faactype", label: "Account Type", options: choices.accountType },
  { name: "facifno", label: "CIF Number", maxLength: 11, numeric: true, optional: true },
  { name: "faacno", label: "Account Number", maxLength: 11, numeric: true, optional: true },
  { name: "faname", label: "Applicant Name", maxLength: 36, alphaOnly: true },
  { name: "fadob", label: "Date of Birth", type: "date" },
  { name: "fagender", label: "Gender", options: choices.gender },
  { name: "famstatus", label: "Marital Status", options: choices.marital },
  { name: "faaadhaarno", label: "Aadhaar Number", maxLength: 12, numeric: true, aadhaar: true },
  { name: "fapan", label: "PAN Number", maxLength: 10, pan: true, optional: true },
  { name: "fafname", label: "Father's Name", maxLength: 36, alphaOnly: true },
  { name: "fasname", label: "Spouse Name", maxLength: 33, alphaOnly: true, spouse: true },
  { name: "famname", label: "Mother's Name", maxLength: 33, alphaOnly: true },
  { name: "fagname", label: "Guardian's Name", maxLength: 36, alphaOnly: true, applicantGuardian: true },
  { name: "fagrelation", label: "Guardian's Relation", options: choices.guardianRelation, applicantGuardian: true },
];

const addressFields = [
  { name: "faadd1", label: "Address Line 1", maxLength: 35 },
  { name: "facity", label: "City", maxLength: 13 },
  { name: "fadist", label: "District", maxLength: 23 },
  { name: "fastate", label: "State", maxLength: 13 },
  { name: "fapin", label: "PIN", maxLength: 6, numeric: true, pin: true },
  { name: "fabplace", label: "Birth Place", maxLength: 13 },
  { name: "famobile", label: "Mobile", maxLength: 10, numeric: true, mobile: true },
  { name: "faincome", label: "Annual Income", maxLength: 8, numeric: true },
  { name: "fareligion", label: "Religion", options: choices.religion },
  { name: "facategory", label: "Category", options: choices.category },
  { name: "faqualification", label: "Qualification", options: choices.qualification },
  { name: "faoccupation", label: "Occupation", options: choices.occupation },
];

const nomineeFields = [
  { name: "nname", label: "Nominee Name", maxLength: 33, alphaOnly: true },
  { name: "nrelation", label: "Relation", options: choices.nomineeRelation },
  { name: "ndob", label: "Date of Birth", type: "date" },
  { name: "ngname", label: "Guardian's Name", maxLength: 33, alphaOnly: true, nomineeGuardian: true },
  { name: "ngage", label: "Guardian's Age", maxLength: 2, numeric: true, nomineeGuardian: true },
];

const allFields = [...personalFields, ...addressFields, ...nomineeFields];
const initialData = Object.fromEntries(allFields.map((field) => [field.name, field.options ? "SELECT" : ""]));

function calculateAge(dateString) {
  if (!dateString) return null;
  const birthDate = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) age -= 1;
  return age;
}

function validateField(field, value, data) {
  const applicantIsMinor = (calculateAge(data.fadob) ?? 18) < 18;
  const nomineeIsMinor = (calculateAge(data.ndob) ?? 18) < 18;
  if (field.spouse && data.famstatus !== "MARRIED") return "";
  if (field.applicantGuardian && !applicantIsMinor) return "";
  if (field.nomineeGuardian && !nomineeIsMinor) return "";

  const text = String(value ?? "").trim();
  if (!field.optional && (!text || text === "SELECT")) return `${field.label} is required.`;
  if (!text || text === "SELECT") return "";
  if (field.alphaOnly && !/^[A-Za-z ]+$/.test(text)) {
    return `${field.label} must contain alphabet letters only.`;
  }
  if (field.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(text)) {
    return "Enter a valid PAN number (e.g. ABCDE1234F).";
  }
  if (field.aadhaar && !/^\d{12}$/.test(text)) {
    return "Aadhaar number must contain exactly 12 digits.";
  }
  if (field.pin && !/^\d{6}$/.test(text)) {
    return "PIN must contain exactly 6 digits.";
  }
  if (field.mobile && !/^[6-9][0-9]{9}$/.test(text)) return "Enter a valid 10-digit mobile number.";
  return "";
}

export default function Single() {
  const [formData, setFormData] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      return saved ? { ...initialData, ...JSON.parse(saved) } : initialData;
    } catch {
      return initialData;
    }
  });
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const nextButtonRef = useRef(null);


  useEffect(() => {
    const controller = new AbortController();

    const loadAutofill = async () => {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent("BOM_AOF-S1")}`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Unable to load autofill details.");
        }

        if (!result.found || !result.data) {
          return;
        }

        setFormData((previous) => ({
          ...previous,

          branch: previous.branch || result.data.branch || "",
          code: previous.code || result.data.code || "",

          facity: previous.facity || result.data.city || "",
          fadist: previous.fadist || result.data.dist || "",
          fastate: previous.fastate || result.data.state || "",
          fapin: previous.fapin || result.data.pin || "",
        }));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("BOM AOF1 autofill error:", error);
        }
      }
    };

    loadAutofill();

    return () => {
      controller.abort();
    };
  }, []);

  const applicantIsMinor = (calculateAge(formData.fadob) ?? 18) < 18;
  const nomineeIsMinor = (calculateAge(formData.ndob) ?? 18) < 18;
  const tabs = useMemo(() => [
    { title: "Applicant Personal Details", fields: personalFields.filter((field) => (!field.spouse || formData.famstatus === "MARRIED") && (!field.applicantGuardian || applicantIsMinor)) },
    { title: "Address and Other Details", fields: addressFields },
    { title: "Nomination Details", fields: nomineeFields.filter((field) => !field.nomineeGuardian || nomineeIsMinor) },
    { title: "Preview", fields: [], preview: true },
  ], [applicantIsMinor, formData.famstatus, nomineeIsMinor]);

  const firstFieldName = tabs[currentTab]?.fields[0]?.name || "";
  const lastFieldName = tabs[currentTab]?.fields.at(-1)?.name || "";

  useEffect(() => {
    if (!firstFieldName) return undefined;
    const frameId = window.requestAnimationFrame(() => {
      document.getElementById(firstFieldName)?.focus();
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [currentTab, firstFieldName]);

  const handleFieldKeyDown = (event, fieldName) => {
    if (fieldName === lastFieldName && event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      nextButtonRef.current?.focus();
    }
  };

  useEffect(() => { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData)); }, [formData]);
  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const handleChange = ({ target: { name, value } }) => {
    const field = allFields.find((item) => item.name === name);
    let cleanValue = value;

    if (field?.numeric) {
      cleanValue = cleanValue.replace(/\D/g, "");
    }

    if (field?.alphaOnly) {
      cleanValue = cleanValue.replace(/[^A-Za-z ]/g, "");
    }

    if (field?.pan) {
      cleanValue = cleanValue.toUpperCase();
    }

    setFormData((previous) => ({ ...previous, [name]: cleanValue }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
    setSubmitError("");
  };

  const validateFields = (fields) => {
    const nextErrors = {};
    fields.forEach((field) => {
      const message = validateField(field, formData[field.name], formData);
      if (message) nextErrors[field.name] = message;
    });
    setErrors((previous) => ({ ...previous, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const nextTab = () => {
    if (!validateFields(tabs[currentTab].fields)) return;
    const next = Math.min(currentTab + 1, tabs.length - 1);
    setCurrentTab(next);
    setMaxAllowedTab((previous) => Math.max(previous, next));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitForm = async () => {
    if (!validateFields(allFields)) {
      setSubmitError("Please correct the highlighted fields before submitting.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const body = new FormData();
      Object.entries(formData).forEach(([key, value]) => body.append(key, value ?? ""));
      const response = await fetch(PDF_ENDPOINT, { method: "POST", body, credentials: "include", cache: "no-store", headers: { Accept: "application/pdf" } });
      const contentType = response.headers.get("content-type") || "";
      if (!response.ok || !contentType.toLowerCase().includes("application/pdf")) {
        const responseText = await response.text();
        throw new Error(responseText.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || `Unable to generate PDF (HTTP ${response.status}).`);
      }
      const blob = await response.blob();
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(URL.createObjectURL(blob));
      setShowPdf(true);
    } catch (error) {
      setSubmitError(error.message || "Unable to generate the PDF.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const error = errors[field.name];

    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}
          {!field.optional && <span className="text-red-500"> *</span>}
        </Label>

        {field.options ? (
          <Select
            value={formData[field.name]}
            onValueChange={(value) =>
              handleChange({ target: { name: field.name, value } })
            }
          >
            <SelectTrigger
              id={field.name}
              className={error ? "border-red-500" : ""}
              onKeyDown={(event) =>
                handleFieldKeyDown(event, field.name)
              }
            >
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id={field.name}
            name={field.name}
            type={field.type || "text"}
            value={formData[field.name]}
            placeholder={field.label}
            maxLength={field.maxLength}
            inputMode={field.numeric ? "numeric" : undefined}
            onChange={handleChange}
            onKeyDown={(event) =>
              handleFieldKeyDown(event, field.name)
            }
            className={error ? "border-red-500" : ""}
            autoComplete="on"
          />
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  };

  if (showPdf && pdfUrl) {
    return (
      <PdfViewerContainer
        pdfUrl={pdfUrl}
        title="BOM Single Account Form"
        backRoute="/Fill_My_Form"
        onBack={() => setShowPdf(false)}
      />
    );
  }

  const previewGroups = tabs
    .filter((tab) => !tab.preview)
    .map((tab) => ({
      title: tab.title,
      rows: tab.fields.map((field) => [
        field.label,
        field.options?.find(([value]) => value === formData[field.name])?.[1]
          || formData[field.name],
      ]),
    }));

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">
              🏦 Bank of Maharashtra Account Opening Form
            </CardTitle>
            <p className="text-muted-foreground">
              Fill customer details carefully.
            </p>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {tabs.map((tab, index) => (
            <Button
              key={tab.title}
              type="button"
              variant={currentTab === index ? "default" : "outline"}
              disabled={index > maxAllowedTab || submitting}
              onClick={() => setCurrentTab(index)}
            >
              {index + 1}. {tab.title.replace(" Details", "")}
            </Button>
          ))}
        </div>

        <div>
          {currentTab < tabs.length - 1 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  {currentTab === 0 && "👤 Applicant Personal Details"}
                  {currentTab === 1 && "🏠 Applicant Address Details"}
                  {currentTab === 2 && "👥 Nomination Details"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tabs[currentTab].fields.map(renderField)}
                </div>
              </CardContent>
            </Card>
          )}

          {currentTab === tabs.length - 1 && (
            <Card>
              <CardHeader>
                <CardTitle>✅ Preview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                {previewGroups.map((group) => (
                  <PreviewSection
                    key={group.title}
                    title={group.title}
                    rows={group.rows}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {submitting && (
            <div className="mt-6 text-center">
              Generating PDF, please wait...
            </div>
          )}

          {submitError && (
            <div className="mt-6 rounded-lg border border-red-500 p-4 text-red-500">
              {submitError}
            </div>
          )}

          <div className="flex justify-between mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCurrentTab((tab) => Math.max(0, tab - 1))}
              disabled={currentTab === 0 || submitting}
            >
              ← Previous
            </Button>

            {currentTab === tabs.length - 1 ? (
              <Button
                type="button"
                onClick={submitForm}
                disabled={submitting}
              >
                {submitting ? "Generating PDF..." : "Submit"}
              </Button>
            ) : (
              <Button
                ref={nextButtonRef}
                type="button"
                onClick={nextTab}
                disabled={submitting}
              >
                {currentTab === tabs.length - 2 ? "Preview" : "Next →"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewSection({ title, rows }) {
  return (
    <div className="rounded-2xl border p-6">
      <h3 className="text-xl font-semibold text-center mb-6">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map(([label, value]) => (
          <div key={label} className="grid grid-cols-2 gap-3 border-b pb-2">
            <div className="font-medium text-muted-foreground">{label}</div>
            <div>{value && value !== "SELECT" ? value : "-"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
